/**
 * PAGE VISIBILITY ENGINE
 * ======================
 * 
 * Moteur de visibilité unifié pour toutes les pages du système.
 * Détermine quelle page est visible où (Admin, Retailer, Footer).
 * 
 * SOURCE DE VÉRITÉ: Page Registry (pageRegistry.js)
 * 
 * SAFE MODE:
 * - Ne modifie AUCUNE route
 * - Ne modifie AUCUN backend
 * - Uniquement logique d'affichage UI
 */

import { PAGE_TYPES } from './pageRegistry';

// ============================================================================
// STATUTS DE PAGE
// ============================================================================

export const PAGE_STATUS = {
  ACTIVE: 'ACTIVE',           // Page fonctionnelle et visible
  RESERVED: 'RESERVED',       // Route réservée mais non implémentée
  ORPHAN: 'ORPHAN',           // Page existante mais route invalide
  LEGACY: 'LEGACY',           // Page obsolète à supprimer
  CMS_ONLY: 'CMS_ONLY',       // Page CMS sans route App.js
  MISSING: 'MISSING'          // Lien existant mais page absente
};

// ============================================================================
// CATÉGORIES MÉTIER
// ============================================================================

export const BUSINESS_CATEGORY = {
  METIER: 'MÉTIER',           // Pages business (Nouveautés, Précommandes, etc.)
  NON_METIER: 'NON_MÉTIER',   // Pages système (Login, Admin, etc.)
  HYBRIDE: 'HYBRIDE'          // Pages structure + business
};

// ============================================================================
// CONFIGURATION COMPLÈTE DES PAGES
// ============================================================================

export const PAGE_VISIBILITY_CONFIG = {
  // ========== PAGES MÉTIER (BUSINESS) ==========
  
  // HUB Pages
  'nouveautes': {
    id: 'nouveautes',
    title: 'Nouveautés',
    route: '/dashboard/nouveautes',
    type: PAGE_TYPES.HUB,
    category: BUSINESS_CATEGORY.METIER,
    status: PAGE_STATUS.ACTIVE,
    visibility: { admin: true, retailer: true, footer: false },
    icon: 'Sparkles',
    order: 1
  },
  'nouveautes-en-cours': {
    id: 'nouveautes-en-cours',
    title: 'Nouveautés - En cours',
    route: '/dashboard/nouveautes/en-cours',
    type: PAGE_TYPES.LISTING,
    category: BUSINESS_CATEGORY.METIER,
    status: PAGE_STATUS.ACTIVE,
    visibility: { admin: true, retailer: false, footer: false },
    parent: 'nouveautes',
    order: 1.1
  },
  'nouveautes-a-venir': {
    id: 'nouveautes-a-venir',
    title: 'Nouveautés - À venir',
    route: '/dashboard/nouveautes/a-venir',
    type: PAGE_TYPES.LISTING,
    category: BUSINESS_CATEGORY.METIER,
    status: PAGE_STATUS.ACTIVE,
    visibility: { admin: true, retailer: false, footer: false },
    parent: 'nouveautes',
    order: 1.2
  },
  
  'precommandes': {
    id: 'precommandes',
    title: 'Pré-commandes',
    route: '/dashboard/precommandes',
    type: PAGE_TYPES.HUB,
    category: BUSINESS_CATEGORY.METIER,
    status: PAGE_STATUS.ACTIVE,
    visibility: { admin: true, retailer: true, footer: false },
    icon: 'CalendarClock',
    order: 2
  },
  'precommandes-en-cours': {
    id: 'precommandes-en-cours',
    title: 'Pré-commandes - Actives',
    route: '/dashboard/precommandes/en-cours',
    type: PAGE_TYPES.LISTING,
    category: BUSINESS_CATEGORY.METIER,
    status: PAGE_STATUS.ACTIVE,
    visibility: { admin: true, retailer: false, footer: false },
    parent: 'precommandes',
    order: 2.1
  },
  'precommandes-a-venir': {
    id: 'precommandes-a-venir',
    title: 'Pré-commandes - À venir',
    route: '/dashboard/precommandes/a-venir',
    type: PAGE_TYPES.LISTING,
    category: BUSINESS_CATEGORY.METIER,
    status: PAGE_STATUS.ACTIVE,
    visibility: { admin: true, retailer: false, footer: false },
    parent: 'precommandes',
    order: 2.2
  },
  
  'offres': {
    id: 'offres',
    title: 'Offres Ponctuelles',
    route: '/dashboard/offres',
    type: PAGE_TYPES.HUB,
    category: BUSINESS_CATEGORY.METIER,
    status: PAGE_STATUS.ACTIVE,
    visibility: { admin: true, retailer: true, footer: false },
    icon: 'Percent',
    order: 3
  },
  'offres-en-cours': {
    id: 'offres-en-cours',
    title: 'Offres - En cours',
    route: '/dashboard/offres/en-cours',
    type: PAGE_TYPES.LISTING,
    category: BUSINESS_CATEGORY.METIER,
    status: PAGE_STATUS.ACTIVE,
    visibility: { admin: true, retailer: false, footer: false },
    parent: 'offres',
    order: 3.1
  },
  
  // Pages métier RÉSERVÉES (non implémentées)
  'selection': {
    id: 'selection',
    title: 'Sélection du Mois',
    route: '/dashboard/selection',
    type: PAGE_TYPES.HUB,
    category: BUSINESS_CATEGORY.METIER,
    status: PAGE_STATUS.RESERVED,
    visibility: { admin: true, retailer: false, footer: false },
    icon: 'Star',
    order: 4
  },
  'bestsellers': {
    id: 'bestsellers',
    title: 'Best-Sellers',
    route: '/dashboard/bestsellers',
    type: PAGE_TYPES.HUB,
    category: BUSINESS_CATEGORY.METIER,
    status: PAGE_STATUS.RESERVED,
    visibility: { admin: true, retailer: false, footer: false },
    icon: 'TrendingUp',
    order: 5
  },
  'tcg': {
    id: 'tcg',
    title: 'TCG',
    route: '/dashboard/tcg',
    type: PAGE_TYPES.HUB,
    category: BUSINESS_CATEGORY.METIER,
    status: PAGE_STATUS.RESERVED,
    visibility: { admin: true, retailer: false, footer: false },
    icon: 'Layers',
    order: 6
  },
  
  // Pages CMS métier
  'partenaires': {
    id: 'partenaires',
    title: 'Partenaires',
    route: '/partners',
    type: PAGE_TYPES.CMS_CONTENT,
    category: BUSINESS_CATEGORY.METIER,
    status: PAGE_STATUS.CMS_ONLY,
    visibility: { admin: true, retailer: true, footer: false },
    icon: 'Handshake',
    order: 7
  },
  'blog': {
    id: 'blog',
    title: 'Blog',
    route: '/dashboard/blog',
    type: PAGE_TYPES.CMS_CONTENT,
    category: BUSINESS_CATEGORY.METIER,
    status: PAGE_STATUS.CMS_ONLY,
    visibility: { admin: true, retailer: true, footer: false },
    icon: 'FileText',
    order: 8
  },
  
  // ========== PAGES NON MÉTIER (SYSTEM) ==========
  
  'dashboard': {
    id: 'dashboard',
    title: 'Accueil',
    route: '/dashboard',
    type: PAGE_TYPES.FUNCTIONAL,
    category: BUSINESS_CATEGORY.HYBRIDE,
    status: PAGE_STATUS.ACTIVE,
    visibility: { admin: true, retailer: true, footer: false },
    icon: 'Home',
    order: 0
  },
  'favoris': {
    id: 'favoris',
    title: 'Ma Wishlist',
    route: '/dashboard/favoris',
    type: PAGE_TYPES.FUNCTIONAL,
    category: BUSINESS_CATEGORY.NON_METIER,
    status: PAGE_STATUS.ACTIVE,
    visibility: { admin: false, retailer: true, footer: false },
    icon: 'Heart',
    order: 10
  },
  'profile': {
    id: 'profile',
    title: 'Mon Compte',
    route: '/dashboard/profil',
    type: PAGE_TYPES.FUNCTIONAL,
    category: BUSINESS_CATEGORY.NON_METIER,
    status: PAGE_STATUS.ACTIVE,
    visibility: { admin: false, retailer: true, footer: false },
    icon: 'User',
    order: 11
  },
  'personal-space': {
    id: 'personal-space',
    title: 'Espace Personnel',
    route: '/dashboard/personal-space',
    type: PAGE_TYPES.FUNCTIONAL,
    category: BUSINESS_CATEGORY.NON_METIER,
    status: PAGE_STATUS.ACTIVE,
    visibility: { admin: false, retailer: true, footer: false },
    icon: 'Folder',
    order: 12
  },
  'search': {
    id: 'search',
    title: 'Recherche',
    route: '/search',
    type: PAGE_TYPES.FUNCTIONAL,
    category: BUSINESS_CATEGORY.NON_METIER,
    status: PAGE_STATUS.ACTIVE,
    visibility: { admin: false, retailer: true, footer: false },
    icon: 'Search',
    order: 13
  },
  
  // Pages légales (MISSING)
  'cgu': {
    id: 'cgu',
    title: 'CGU',
    route: '/cgu',
    type: PAGE_TYPES.PUBLIC,
    category: BUSINESS_CATEGORY.NON_METIER,
    status: PAGE_STATUS.MISSING,
    visibility: { admin: true, retailer: false, footer: true },
    order: 100
  },
  'cgv': {
    id: 'cgv',
    title: 'CGV',
    route: '/cgv',
    type: PAGE_TYPES.PUBLIC,
    category: BUSINESS_CATEGORY.NON_METIER,
    status: PAGE_STATUS.MISSING,
    visibility: { admin: true, retailer: false, footer: true },
    order: 101
  },
  'mentions-legales': {
    id: 'mentions-legales',
    title: 'Mentions Légales',
    route: '/mentions-legales',
    type: PAGE_TYPES.PUBLIC,
    category: BUSINESS_CATEGORY.NON_METIER,
    status: PAGE_STATUS.MISSING,
    visibility: { admin: true, retailer: false, footer: true },
    order: 102
  },
  'confidentialite': {
    id: 'confidentialite',
    title: 'Politique de Confidentialité',
    route: '/confidentialite',
    type: PAGE_TYPES.PUBLIC,
    category: BUSINESS_CATEGORY.NON_METIER,
    status: PAGE_STATUS.MISSING,
    visibility: { admin: true, retailer: false, footer: true },
    order: 103
  }
};

// ============================================================================
// VISIBILITY RULES
// ============================================================================

/**
 * Règles de visibilité par statut
 */
const VISIBILITY_RULES = {
  [PAGE_STATUS.ACTIVE]: {
    admin: true,
    retailer: true,
    footer: true
  },
  [PAGE_STATUS.RESERVED]: {
    admin: true,     // Visible avec badge "Réservé"
    retailer: false, // Caché
    footer: false
  },
  [PAGE_STATUS.ORPHAN]: {
    admin: true,     // Visible avec warning
    retailer: false,
    footer: false
  },
  [PAGE_STATUS.LEGACY]: {
    admin: false,    // Complètement caché
    retailer: false,
    footer: false
  },
  [PAGE_STATUS.CMS_ONLY]: {
    admin: true,
    retailer: true,  // Visible si show_in_menu=true dans CMS
    footer: false
  },
  [PAGE_STATUS.MISSING]: {
    admin: true,     // Visible avec warning "Page manquante"
    retailer: false,
    footer: true     // Le lien doit exister mais pointera vers 404
  }
};

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Récupère toutes les pages visibles pour un contexte donné
 * @param {'admin'|'retailer'|'footer'} context
 * @returns {Array} Liste des pages visibles
 */
export const getVisiblePages = (context) => {
  return Object.values(PAGE_VISIBILITY_CONFIG)
    .filter(page => {
      // Vérifier la visibilité configurée
      if (!page.visibility[context]) return false;
      
      // Appliquer les règles par statut
      const rules = VISIBILITY_RULES[page.status];
      if (!rules || !rules[context]) return false;
      
      return true;
    })
    .sort((a, b) => a.order - b.order);
};

/**
 * Récupère les pages métier pour le menu retailer
 */
export const getRetailerMenuPages = () => {
  return getVisiblePages('retailer')
    .filter(page => 
      page.category === BUSINESS_CATEGORY.METIER && 
      page.status === PAGE_STATUS.ACTIVE
    );
};

/**
 * Récupère les pages HUB pour le menu principal
 */
export const getHubMenuPages = () => {
  return Object.values(PAGE_VISIBILITY_CONFIG)
    .filter(page => 
      page.type === PAGE_TYPES.HUB && 
      page.status === PAGE_STATUS.ACTIVE &&
      page.visibility.retailer
    )
    .sort((a, b) => a.order - b.order);
};

/**
 * Récupère les pages pour l'admin avec statuts
 */
export const getAdminPagesWithStatus = () => {
  return Object.values(PAGE_VISIBILITY_CONFIG)
    .filter(page => page.visibility.admin)
    .map(page => ({
      ...page,
      statusBadge: getStatusBadge(page.status),
      categoryBadge: getCategoryBadge(page.category)
    }))
    .sort((a, b) => a.order - b.order);
};

/**
 * Récupère les pages pour le footer
 */
export const getFooterPages = () => {
  return Object.values(PAGE_VISIBILITY_CONFIG)
    .filter(page => page.visibility.footer)
    .sort((a, b) => a.order - b.order);
};

/**
 * Vérifie si une page doit être visible dans un contexte
 */
export const isPageVisibleIn = (pageId, context) => {
  const page = PAGE_VISIBILITY_CONFIG[pageId];
  if (!page) return false;
  
  if (!page.visibility[context]) return false;
  
  const rules = VISIBILITY_RULES[page.status];
  return rules && rules[context];
};

/**
 * Obtient le badge de statut pour l'affichage
 */
export const getStatusBadge = (status) => {
  const badges = {
    [PAGE_STATUS.ACTIVE]: { label: 'Actif', color: 'bg-green-100 text-green-700 border-green-200' },
    [PAGE_STATUS.RESERVED]: { label: 'Réservé', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    [PAGE_STATUS.ORPHAN]: { label: 'Orphelin', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    [PAGE_STATUS.LEGACY]: { label: 'Legacy', color: 'bg-gray-100 text-gray-500 border-gray-200' },
    [PAGE_STATUS.CMS_ONLY]: { label: 'CMS', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    [PAGE_STATUS.MISSING]: { label: 'Manquant', color: 'bg-red-100 text-red-700 border-red-200' }
  };
  return badges[status] || { label: status, color: 'bg-gray-100 text-gray-500' };
};

/**
 * Obtient le badge de catégorie pour l'affichage
 */
export const getCategoryBadge = (category) => {
  const badges = {
    [BUSINESS_CATEGORY.METIER]: { label: 'Métier', color: 'bg-purple-100 text-purple-700 border-purple-200' },
    [BUSINESS_CATEGORY.NON_METIER]: { label: 'Système', color: 'bg-slate-100 text-slate-700 border-slate-200' },
    [BUSINESS_CATEGORY.HYBRIDE]: { label: 'Hybride', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' }
  };
  return badges[category] || { label: category, color: 'bg-gray-100 text-gray-500' };
};

/**
 * Obtient une page par son ID
 */
export const getPageConfig = (pageId) => {
  return PAGE_VISIBILITY_CONFIG[pageId] || null;
};

/**
 * Obtient une page par sa route
 */
export const getPageByRoute = (route) => {
  const normalized = route.toLowerCase().replace(/\/$/, '');
  return Object.values(PAGE_VISIBILITY_CONFIG).find(
    page => page.route.toLowerCase() === normalized
  ) || null;
};

export default {
  PAGE_STATUS,
  BUSINESS_CATEGORY,
  PAGE_VISIBILITY_CONFIG,
  getVisiblePages,
  getRetailerMenuPages,
  getHubMenuPages,
  getAdminPagesWithStatus,
  getFooterPages,
  isPageVisibleIn,
  getStatusBadge,
  getCategoryBadge,
  getPageConfig,
  getPageByRoute
};
